import { Localized } from "@fluent/react"
import { LoadingButton, Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, timelineItemClasses, TimelineSeparator } from "@mui/lab"
import { Alert, Avatar, Box, Button, ButtonGroup, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, List, ListItem, ListItemAvatar, ListItemText, Skeleton, TextField, Tooltip, Typography } from "@mui/material"
import { Stack } from "@mui/system"
import { DragEvent, useCallback, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { If } from "../../app/If"
import FileCopyIcon from '@mui/icons-material/FileCopy'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HistoryIcon from '@mui/icons-material/History'
import { BlobMeta, BlobVersion, deleteBlob, getBlobVersionUrl, loadBlobVersions, selectBlobsByResourceId, uploadBlobs, uploadBlobVersion, Uuid } from "../documents/attachementsSlice"
import { BlobLink } from "../documents/BlobLink"
import { formatDate } from "../../app/Utils"
import { selectUserById } from "../users/usersSlice"
import { CloseButton } from "../../app/components/CloseButton"
import { APIError } from "../../app/appTypes"


export interface AttachementsProps {
    resourceId: Uuid
    canUpload: boolean
    canDelete: boolean
    /** Object metadata persisted on uploaded blobs (see {@link BlobResourceType}). */
    resourceType?: string
    parentResourceId?: string
    parentResourceType?: string
    /**
     * Whether every attachment change has to carry a justification. Pass
     * `header.status !== DocumentStatus.NEW` from screens that belong to a document; the API enforces
     * the same rule and rejects with 400, so this only moves the feedback earlier. Screens with no
     * document behind them (projects, devices, specs…) leave it unset.
     */
    requireRemarks?: boolean
}

/** Pulls the server's explanation out of a rejected thunk, falling back to a generic message. */
const errorDetail = (error: unknown): string | null => {
    const problem = (error as APIError | undefined)
    if (problem && problem.kind === 'http' && problem.problem?.detail) {
        return problem.problem.detail
    }
    return null
}

export const Attachements = (props: AttachementsProps) => {
    const { resourceId } = props
    const { blobs } = useAppSelector(state => selectBlobsByResourceId(state, resourceId) ?? { resourceId, blobs: [] })

    if (blobs === null) {
        return <Skeleton height={200} />
    } else {
        return <View blobs={blobs} {...props} />
    }
}

interface ViewProps extends AttachementsProps {
    blobs: BlobMeta[]
}

const View = ({ blobs, canUpload, canDelete, resourceId, resourceType, parentResourceId, parentResourceType, requireRemarks }: ViewProps) => {
    const dispatch = useAppDispatch()
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const formRef = useRef<HTMLFormElement | null>(null)
    const dropboxRef = useRef<HTMLDivElement | null>(null)
    const [uploading, setUploading] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState<FileList | never[] | null>(null)
    const [remarks, setRemarks] = useState('')
    const [uploadError, setUploadError] = useState<string | null>(null)
    const [pendingDelete, setPendingDelete] = useState<BlobMeta | null>(null)

    const handleSelectFilesClick = useCallback(() => {
        if (fileInputRef) {
            fileInputRef.current?.click()
        }
    }, [fileInputRef])
    const handleSelectedFilesChange = useCallback(() => {
        if (fileInputRef) {
            setSelectedFiles(fileInputRef.current?.files ?? null)
        }
    }, [fileInputRef, setSelectedFiles])
    const handleCancelUploadClick = useCallback(() => {
        if (formRef) {
            formRef.current?.reset()
            setSelectedFiles(null)
            setRemarks('')
            setUploadError(null)
        }
    }, [formRef, setSelectedFiles])
    const handleUploadClick = async () => {
        if (selectedFiles && selectedFiles.length > 0) {
            setUploading(true)
            setUploadError(null)
            let files: File[] = []
            for (const file of selectedFiles) {
                files.push(file)
            }
            try {
                await dispatch(uploadBlobs([resourceId, files, { resourceType, parentResourceId, parentResourceType, remarks }])).unwrap()
                if (formRef) {
                    formRef.current?.reset()
                }
                setSelectedFiles(null)
                setRemarks('')
            } catch (error) {
                console.log(error)
                setUploadError(errorDetail(error))
            } finally {
                setUploading(false)
            }
        }
    }

    // Mirrors the server rule so the Upload button is disabled rather than the request bouncing.
    const remarksMissing = (requireRemarks ?? false) && remarks.trim().length === 0

    const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])
    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])
    const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
        const dt = e.dataTransfer
        if (dt) {
            setSelectedFiles(dt.files)
        }
    }, [])

    const handleDeleteConfirmed = useCallback(async (reason: string) => {
        if (!pendingDelete) return
        await dispatch(deleteBlob([resourceId, pendingDelete.blobId, reason])).unwrap()
        setPendingDelete(null)
    }, [dispatch, resourceId, pendingDelete])

    const anyFiles: boolean = selectedFiles !== null && selectedFiles.length > 0 ? true : false


    return <Stack spacing={2}>
        <If condition={blobs.length > 0} otherwise={<Typography gutterBottom component="div" color="text.secondary"><Localized id="no-attachements">Brak zalączników</Localized></Typography>}>
            <Box>
                <List>
                    {blobs.map(blob =>
                        <ListItem key={blob.blobId} secondaryAction={
                            <Stack direction='row'>
                                <If condition={blob.version > 1}>
                                    <BlobHistoryButton resourceId={resourceId} blobId={blob.blobId} fileName={blob.fileMeta.fileName} />
                                </If>
                                <If condition={canUpload}>
                                    <UploadNewVersionButton resourceId={resourceId} blobId={blob.blobId} requireRemarks={requireRemarks} />
                                </If>
                                <BlobLink blobId={blob.blobId} target="_blank">
                                    <IconButton><DownloadIcon /></IconButton>
                                </BlobLink>
                                <If condition={canDelete}>
                                    <Tooltip title={<Localized id="delete">Usuń</Localized>}>
                                        <IconButton onClick={() => setPendingDelete(blob)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </If>
                            </Stack>
                        }>
                            <ListItemAvatar>
                                <Avatar>
                                    <FileCopyIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        {/* The document-wide attachment number, as printed on the report. */}
                                        <If condition={blob.sequenceNumber !== null}>
                                            <Typography component="span" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 24 }}>
                                                {blob.sequenceNumber}.
                                            </Typography>
                                        </If>
                                        <span>{blob.fileMeta.fileName}</span>
                                        <If condition={blob.version > 1}>
                                            <Chip size="small" variant="outlined" label={`v${blob.version}`} />
                                        </If>
                                    </Stack>
                                }
                                secondary={<AttachmentDetails blob={blob} />}
                            />
                        </ListItem>)}
                </List>
            </Box>
        </If>
        <Box>
            <form ref={formRef}>
                <input
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    type="file"
                    multiple
                    onChange={handleSelectedFilesChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
            </form>
            <If condition={canUpload}>
                <Button size="large" variant="contained" onClick={handleSelectFilesClick}>
                    <Localized id="select-files">Wybierz pliki</Localized>
                </Button>
            </If>
        </Box>
        <If condition={canUpload}>
            <Box sx={{
                width: '100%',
                minHeight: 60,
                border: 'dashed grey',
                marginTop: 2,
                padding: 1,
            }} ref={dropboxRef} component="div" onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDrop={handleDrop}>
                <Typography variant="body1" color="GrayText" gutterBottom>
                    <Localized id="drop-files">or drop here...</Localized>
                </Typography>
                <If condition={anyFiles}>
                    <FileList files={selectedFiles} />
                </If>
            </Box>
        </If>
        <TotalSizeExcceedsLimit files={selectedFiles} mbs={16} />
        <If condition={anyFiles}>
            <Stack spacing={2}>
                <If condition={canUpload}>
                    <RemarksField
                        value={remarks}
                        onChange={setRemarks}
                        required={requireRemarks ?? false}
                        disabled={uploading}
                    />
                </If>
                <If condition={uploadError !== null}>
                    <Alert severity="error">{uploadError}</Alert>
                </If>
                <ButtonGroup>
                    <LoadingButton loading={uploading} disabled={remarksMissing} size="large" variant="contained" onClick={handleUploadClick}>
                        <Localized id="upload">Wyślij</Localized>
                    </LoadingButton>
                    <LoadingButton loading={uploading} size="large" variant="contained" onClick={handleCancelUploadClick}>
                        <Localized id="cancel">Anuluj</Localized>
                    </LoadingButton>
                </ButtonGroup>
            </Stack>
        </If>
        <DeleteAttachmentDialog
            blob={pendingDelete}
            requireRemarks={requireRemarks ?? false}
            onCancel={() => setPendingDelete(null)}
            onConfirm={handleDeleteConfirmed}
        />
    </Stack>
}

/**
 * Secondary line of an attachment row: content type, plus who uploaded the version being served and
 * when. For a re-versioned file that is the latest version's uploader, matching what the row links
 * to — the per-version breakdown lives in the history dialog.
 */
const AttachmentDetails = ({ blob }: { blob: BlobMeta }) => {
    const { contentType } = blob.fileMeta
    const uploadedBy = blob.uploadedByFullName?.trim() || blob.uploadedBy?.trim()
    const uploadedAt = blob.uploadedAt ? formatDate(new Date(blob.uploadedAt), true) : null

    // Older files can be missing this metadata; show only the parts that exist.
    const attribution = [uploadedBy, uploadedAt].filter(Boolean).join(' · ')

    return <>{[contentType, attribution].filter(Boolean).join(' · ')}</>
}

/**
 * The justification captured alongside an attachment change. Marked required only where the caller
 * says so; the API rejects a missing one for documents past status NEW either way.
 */
const RemarksField = ({ value, onChange, required, disabled }: {
    value: string,
    onChange: (value: string) => void,
    required: boolean,
    disabled?: boolean,
    // helperText is localized only when required, so the "required" hint does not leak onto screens
    // where remarks are optional.
}) => <Localized id="attachment-remarks" attrs={{ label: true, helperText: required }}>
        <TextField
            label="Uwagi"
            helperText={required ? "Wymagane dla dokumentu poza statusem Nowy" : undefined}
            value={value}
            onChange={e => onChange(e.target.value)}
            required={required}
            disabled={disabled}
            multiline
            minRows={2}
            fullWidth
            inputProps={{ maxLength: 1000 }}
        />
    </Localized>

/** Confirms an attachment deletion and, where the rule applies, collects the reason for it. */
const DeleteAttachmentDialog = ({ blob, requireRemarks, onCancel, onConfirm }: {
    blob: BlobMeta | null,
    requireRemarks: boolean,
    onCancel: () => void,
    onConfirm: (reason: string) => Promise<void>,
}) => {
    const [reason, setReason] = useState('')
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Reset per attachment so a previous reason never leaks into the next deletion.
    const handleCancel = useCallback(() => {
        setReason('')
        setError(null)
        onCancel()
    }, [onCancel])

    const handleConfirm = useCallback(async () => {
        setDeleting(true)
        setError(null)
        try {
            await onConfirm(reason)
            setReason('')
        } catch (e) {
            setError(errorDetail(e))
        } finally {
            setDeleting(false)
        }
    }, [onConfirm, reason])

    const reasonMissing = requireRemarks && reason.trim().length === 0

    return <Dialog open={blob !== null} onClose={deleting ? undefined : handleCancel} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
            <DeleteIcon color="error" />
            <Localized id="attachment-delete-title">Usunąć załącznik?</Localized>
        </DialogTitle>
        <DialogContent dividers>
            <Stack spacing={2}>
                <Typography variant="body1">{blob?.fileMeta.fileName}</Typography>
                <RemarksField value={reason} onChange={setReason} required={requireRemarks} disabled={deleting} />
                <If condition={error !== null}>
                    <Alert severity="error">{error}</Alert>
                </If>
            </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCancel} disabled={deleting}>
                <Localized id="cancel">Anuluj</Localized>
            </Button>
            <LoadingButton loading={deleting} disabled={reasonMissing} onClick={handleConfirm} color="error" variant="contained">
                <Localized id="delete">Usuń</Localized>
            </LoadingButton>
        </DialogActions>
    </Dialog>
}

type UploadStatus = 'idle' | 'form' | 'uploading' | 'success' | 'error'

/**
 * New-version upload. The dialog opens on click and owns the whole flow — pick or drop the file,
 * add the justification, send — rather than firing the OS file picker first and only then asking
 * for anything else.
 */
const UploadNewVersionButton = ({ resourceId, blobId, requireRemarks }: { resourceId: Uuid, blobId: string, requireRemarks?: boolean }) => {
    const dispatch = useAppDispatch()
    const inputRef = useRef<HTMLInputElement | null>(null)
    const [status, setStatus] = useState<UploadStatus>('idle')
    const [file, setFile] = useState<File | null>(null)
    const [remarks, setRemarks] = useState('')
    const [error, setError] = useState<string | null>(null)

    const uploading = status === 'uploading'

    const handleOpen = useCallback(() => {
        setFile(null)
        setRemarks('')
        setError(null)
        setStatus('form')
    }, [])

    const handlePickFileClick = useCallback(() => {
        inputRef.current?.click()
    }, [])

    const handleChange = useCallback(() => {
        const picked = inputRef.current?.files?.[0] ?? null
        // Reset the input so re-selecting the same file still fires onChange.
        if (inputRef.current) inputRef.current.value = ''
        if (picked) setFile(picked)
    }, [])

    const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
        // A version is a single file; ignore everything after the first if several are dropped.
        const dropped = e.dataTransfer?.files?.[0]
        if (dropped) setFile(dropped)
    }, [])

    const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.preventDefault()
    }, [])

    const handleSend = useCallback(async () => {
        if (!file) return
        setStatus('uploading')
        setError(null)
        try {
            await dispatch(uploadBlobVersion([resourceId, blobId, file, remarks])).unwrap()
            setStatus('success')
        } catch (e) {
            console.log(e)
            setError(errorDetail(e))
            setStatus('error')
        }
    }, [dispatch, resourceId, blobId, file, remarks])

    const handleCloseDialog = useCallback(() => {
        setStatus('idle')
        setFile(null)
        setRemarks('')
        setError(null)
    }, [])

    const cannotSend = file === null || ((requireRemarks ?? false) && remarks.trim().length === 0)

    return <>
        <input
            ref={inputRef}
            style={{ display: 'none' }}
            type="file"
            onChange={handleChange}
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        />
        <Tooltip title={<Localized id="upload-new-version">Wyślij nową wersję</Localized>}>
            <span>
                <IconButton onClick={handleOpen} disabled={uploading}>
                    <UploadFileIcon />
                </IconButton>
            </span>
        </Tooltip>
        <Dialog
            open={status !== 'idle'}
            onClose={uploading ? undefined : handleCloseDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                <UploadFileIcon color="primary" />
                <Localized id="upload-new-version">Wyślij nową wersję</Localized>
            </DialogTitle>
            <DialogContent dividers>
                <If condition={status === 'form'} otherwise={
                    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: 200, textAlign: 'center', px: 2 }}>
                        <If condition={uploading}>
                            <CircularProgress size={56} thickness={4} />
                            <Typography variant="body1" color="text.secondary">
                                <Localized id="upload-new-version-uploading">Wysyłanie nowej wersji…</Localized>
                            </Typography>
                        </If>
                        <If condition={status === 'success'}>
                            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 72 }} />
                            <Typography variant="h6">
                                <Localized id="upload-new-version-success">Nowa wersja została wysłana</Localized>
                            </Typography>
                        </If>
                        <If condition={status === 'error'}>
                            <ErrorOutlineIcon color="error" sx={{ fontSize: 72 }} />
                            <Typography variant="h6">
                                <Localized id="upload-new-version-error">Nie udało się wysłać nowej wersji</Localized>
                            </Typography>
                            <If condition={error !== null}>
                                <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>{error}</Alert>
                            </If>
                        </If>
                    </Stack>
                }>
                    <Stack spacing={2} sx={{ py: 1 }}>
                        <Box
                            onClick={handlePickFileClick}
                            onDragEnter={handleDragOver}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            sx={{
                                border: '1px dashed',
                                borderColor: file ? 'primary.main' : 'grey.500',
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center',
                                cursor: 'pointer',
                                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                            }}
                        >
                            <UploadFileIcon color={file ? 'primary' : 'disabled'} sx={{ fontSize: 40 }} />
                            <If condition={file !== null} otherwise={
                                <Typography variant="body1" color="text.secondary">
                                    <Localized id="select-or-drop-file">Wybierz plik lub upuść go tutaj</Localized>
                                </Typography>
                            }>
                                <Stack spacing={0.5}>
                                    <Typography variant="body1">{file?.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {file ? formatBytes(file.size) : null}
                                    </Typography>
                                </Stack>
                            </If>
                        </Box>
                        <RemarksField value={remarks} onChange={setRemarks} required={requireRemarks ?? false} />
                    </Stack>
                </If>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <If condition={status === 'form'} otherwise={
                    <Button onClick={handleCloseDialog} disabled={uploading} variant="contained">
                        <Localized id="close">Zamknij</Localized>
                    </Button>
                }>
                    <>
                        <Button onClick={handleCloseDialog}>
                            <Localized id="cancel">Anuluj</Localized>
                        </Button>
                        <LoadingButton onClick={handleSend} disabled={cannotSend} variant="contained">
                            <Localized id="upload">Wyślij</Localized>
                        </LoadingButton>
                    </>
                </If>
            </DialogActions>
        </Dialog>
    </>
}

function formatBytes(size: number): string {
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

const BlobHistoryButton = ({ resourceId, blobId, fileName }: { resourceId: Uuid, blobId: string, fileName: string }) => {
    const dispatch = useAppDispatch()
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [versions, setVersions] = useState<BlobVersion[] | null>(null)
    const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null)

    const handleOpen = useCallback(async () => {
        setOpen(true)
        setLoading(true)
        setError(false)
        try {
            const vs = await dispatch(loadBlobVersions([resourceId, blobId])).unwrap()
            setVersions(vs)
        } catch (e) {
            console.log(e)
            setError(true)
        } finally {
            setLoading(false)
        }
    }, [dispatch, resourceId, blobId])

    const handleClose = useCallback(() => setOpen(false), [])

    const handleDownload = useCallback(async (version: number) => {
        setDownloadingVersion(version)
        try {
            const url = await dispatch(getBlobVersionUrl([resourceId, blobId, version])).unwrap()
            window.open(url, '_blank', 'noopener,noreferrer')
        } catch (e) {
            console.log(e)
        } finally {
            setDownloadingVersion(null)
        }
    }, [dispatch, resourceId, blobId])

    const latest = versions && versions.length > 0 ? versions[0].version : null

    return <>
        <Tooltip title={<Localized id="version-history">Historia wersji</Localized>}>
            <IconButton onClick={handleOpen}><HistoryIcon /></IconButton>
        </Tooltip>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                <HistoryIcon color="primary" />
                <Localized id="version-history">Historia wersji</Localized>
            </DialogTitle>
            <DialogContent dividers>
                {/* The file the history belongs to, so the dialog reads on its own once it covers the list. */}
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <Avatar variant="rounded" sx={{ bgcolor: 'action.hover', color: 'text.secondary', width: 40, height: 40 }}>
                        <FileCopyIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            <Localized id="attachment-file-name">Nazwa pliku</Localized>
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600} noWrap title={fileName}>{fileName}</Typography>
                    </Box>
                </Stack>
                <If condition={loading}>
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                </If>
                <If condition={error}>
                    <Alert severity="error"><Localized id="version-history-error">Nie udało się wczytać historii wersji</Localized></Alert>
                </If>
                <If condition={!loading && !error && versions !== null}>
                    {/* Newest first, as the API returns them, so the timeline reads top-down as
                        "where the file stands now, and how it got there". The `:before` override is
                        MUI's recipe for a left-aligned timeline with no opposite content — without it
                        every item reserves half the dialog for a column we do not use. */}
                    <Timeline
                        position="right"
                        sx={{
                            m: 0,
                            p: 0,
                            [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
                        }}
                    >
                        {(versions ?? []).map((v, idx) =>
                            <TimelineItem key={v.version}>
                                <TimelineSeparator>
                                    <TimelineDot
                                        color={v.version === latest ? 'primary' : 'grey'}
                                        variant={v.version === latest ? 'filled' : 'outlined'}
                                    />
                                    {/* No tail below the oldest version — the history starts there. */}
                                    <If condition={idx < (versions ?? []).length - 1}>
                                        <TimelineConnector />
                                    </If>
                                </TimelineSeparator>
                                {/* Keep MUI's default top padding: it is what lines the first row up
                                    with the dot. Only the trailing gap between versions is ours. */}
                                <TimelineContent sx={{ pr: 0, pb: 3 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography variant="subtitle2" fontWeight={700}>{`v${v.version}`}</Typography>
                                        <If condition={v.version === latest}>
                                            <Chip size="small" color="primary" label={<Localized id="version-current">Aktualna</Localized>} />
                                        </If>
                                        <Box sx={{ flexGrow: 1 }} />
                                        <Tooltip title={<Localized id="download">Pobierz</Localized>}>
                                            <span>
                                                <IconButton size="small" onClick={() => handleDownload(v.version)} disabled={downloadingVersion !== null}>
                                                    <If condition={downloadingVersion === v.version} otherwise={<DownloadIcon fontSize="small" />}>
                                                        <CircularProgress size={18} />
                                                    </If>
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                    <BlobVersionDetails {...v} />
                                </TimelineContent>
                            </TimelineItem>
                        )}
                    </Timeline>
                </If>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <CloseButton loading={false} onClick={handleClose} />
            </DialogActions>
        </Dialog>
    </>
}

const BlobVersionDetails = (props: BlobVersion) => {
    const { uploadedAt, uploadedByFullName, sizeBytes, remarks } = props
    const uploadedAtLabel = formatDate(new Date(uploadedAt), true)
    const uploadedByLabel = uploadedByFullName?.trim() || 'unknown'
    const remark = remarks?.trim()

    return <>
        <Typography variant="body2" color="text.secondary">
            {`${uploadedAtLabel} · ${uploadedByLabel} · ${formatBytes(sizeBytes)}`}
        </Typography>
        {/* The justification recorded with this version. Always shown, including when there is none:
            "no remark was given" is itself audit-relevant, and silence would be indistinguishable
            from the field not existing. Versions predating the feature, and uploads made while the
            document was still in the NEW status, legitimately have none — so the empty case stays a
            quiet one-liner, and only a real remark earns the callout block. */}
        <If
            condition={!!remark}
            otherwise={
                <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                    <Localized id="version-remarks">Uwagi:</Localized>
                    {' '}
                    <Localized id="version-remarks-none">Brak</Localized>
                </Typography>
            }
        >
            <Box sx={{
                mt: 1,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                borderLeft: 3,
                borderColor: 'primary.main',
            }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <Localized id="version-remarks">Uwagi:</Localized>
                </Typography>
                {/* Remarks are captured in a multiline field; keep the author's line breaks. */}
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {remark}
                </Typography>
            </Box>
        </If>
    </>
}

const TotalSizeExcceedsLimit = ({ files, mbs }: { files: FileList | never[] | null, mbs: number }) => {
    if (files === null) {
        return null
    }

    let size = 0
    for (const file of files) {
        size += file.size
    }
    if (size > 1024 * 1024 * mbs) {
        if (files.length === 1) {
            return <Alert severity="error">
                <Typography color="error">
                    <Localized id='error-file-too-large' vars={{ size: mbs }}>Some text</Localized>
                </Typography>
            </Alert>
        } else {
            return <Alert severity="error">
                <Typography color="error">
                    <Localized id='error-total-size-exceeded' vars={{ size: mbs }}></Localized>
                </Typography>
            </Alert>
        }
    } else {
        return null
    }
}

function fileSizeKBorMB(file: File): string {
    let size = file.size
    if (size < 1024) {
        return `${size}B`
    } else if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)}KB`
    } else {
        return `${(size / (1024 * 1024)).toFixed(2)}MB`
    }
}

export function isFileAnImage(file: File): boolean {
    return file.type.startsWith('image/')
}

const FileImage = ({ file }: { file: File }) => {
    if (isFileAnImage(file)) {
        const url = URL.createObjectURL(file)
        return <img
            style={{ width: '90px' }}
            src={url}
            onLoad={() => URL.revokeObjectURL(url)}
        />
    } else {
        return <UploadFileIcon />
    }
}

const FileList = ({ files }: { files: FileList | never[] | null }) => {
    if (files === null) {
        return <span>No files</span>
    }

    let idx: number[] = []
    for (let i = 0; i < files.length; i++) {
        idx.push(i)
    }

    return <List>{idx.map(j => {
        const file = files[j]
        return <ListItem>
            <ListItemAvatar>
                <FileImage file={file} />
            </ListItemAvatar>
            <ListItemText primary={file.name} secondary={`size: ${fileSizeKBorMB(file)}`}></ListItemText>
        </ListItem>
    })}</List>
}

